'use es6';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Set as ImmutableSet } from 'immutable';
import { parse, stringify } from 'hub-http/helpers/params';
import UsageTracker from '../utils/UsageTracker';
import { FILTER_TYPES_OTHER } from '../constants/TopFilterConstants';
import { PARTNER_TRACKS_URL_FRAGMENT, RESULTS_URL_FRAGMENT } from '../constants/Navigation';
/**
 * Input element types (real or simulated) that can be deselected,
 * e.g. a checkbox being unchecked. This could also include 'radio' but we're
 * not using those anywhere at the moment.
 *
 * This is necessary because all input elements have a "checked" property
 * which defaults to false, including text input / search fields.
 */
const DESELECTABLES = ImmutableSet(['checkbox', 'SideNavItem']);
export const parseAllParams = search => parse(search.slice(1));
export const parseValue = (key, search = window.location.search) => {
  const value = parseAllParams(search)[key];
  return value ? Array.isArray(value) ? value : [value] : [];
};
const clearCatalogFilterParams = params => {
  delete params[FILTER_TYPES_OTHER.CATEGORIES];
  delete params[FILTER_TYPES_OTHER.TOOLS];
  delete params['page'];
  return params;
};
const clearSearchFilterParam = params => {
  delete params[FILTER_TYPES_OTHER.SEARCH];
  return params;
};
const isPartnersPage = location => {
  return location && location.pathname && location.pathname.includes(PARTNER_TRACKS_URL_FRAGMENT);
};
const trackCategoryFilterChange = (checked, inputVal, key) => {
  UsageTracker.track('filterInteraction', {
    action: checked ? 'filter-selected' : 'filter-deselected',
    value: inputVal,
    key
  });
};

/**
 * Get a new value (array) for the param.
 */
export const getNewValue = (isDeselected, value, replace, inputVal) => (isDeselected ? replace ? ImmutableSet() : ImmutableSet(value).delete(inputVal) : replace ? ImmutableSet([inputVal]) : ImmutableSet(value).add(inputVal)).toArray();

/**
 * Custom hook to get and set a url param.
 *
 * @param {string} key Param name
 * @param {boolean} [clearCatalogFilers=false] Optional flag to remove catalog filters
 * @returns {[string[], function]} Array with [valueArr, setValue]
 */
export const useSearchParam = (key, clearCatalogFilers = false, goToResults = false) => {
  const history = useHistory();
  const location = useLocation();
  const value = useMemo(() => parseValue(key, location.search), [key, location.search]);
  const setValue = useCallback(newValue => {
    const allParams = clearCatalogFilers ? clearCatalogFilterParams(parseAllParams(location.search)) : parseAllParams(location.search);
    history.push(Object.assign({}, location, {
      pathname: goToResults ? RESULTS_URL_FRAGMENT : location.pathname,
      search: stringify(Object.assign(allParams, {
        [key]: newValue
      }))
    }));
  }, [clearCatalogFilers, goToResults, history, key, location]);
  return [value, setValue];
};

/**
 * Custom hook to get and set a url param - the setter can be used directly
 * as a change handler for an input element.
 *
 * @param {string} key Param name
 * @param {boolean} [replace=false] Optional flag to replace existing values
 * @param {boolean} [clearCatalogFilers=false] Optional flag to remove catalog filters
 * @returns {[string[], function]} Array with [valueArr, inputChangeHandler]
 */
export const useSearchParamInput = (key, replace = false, clearCatalogFilers = false, goToResults = false) => {
  const [value, setValue] = useSearchParam(key, clearCatalogFilers, goToResults);
  const inputChangeHandler = useCallback(({
    target: {
      checked,
      type,
      value: inputVal
    }
  }) => {
    setValue(getNewValue(DESELECTABLES.includes(type) && !checked || inputVal === '', value, replace, inputVal));
    if (DESELECTABLES.includes(type)) {
      trackCategoryFilterChange(checked, inputVal, key);
    }
  }, [setValue, value, replace, key]);
  return [value, inputChangeHandler];
};

/**
 * Use an input field value with a debounced change handler.
 *
 * @param {string} key Param name
 * @param {number} timeout
 * @param {boolean} [clearCatalogFilers=false] Optional flag to remove catalog filters on search
 */
export const useDebouncedTextInput = (key, timeout, clearCatalogFilers = false, goToResults) => {
  const [paramValue, changeHandler] = useSearchParamInput(key, true, clearCatalogFilers, goToResults);
  const selected = paramValue[0] || '';
  const [inputValue, setValue] = useState(selected);
  const lastReported = useRef(selected);
  const lastTimeout = useRef();
  const onChange = useCallback(({
    target: {
      value
    }
  }) => {
    setValue(value);
    window.clearTimeout(lastTimeout.current);
    lastTimeout.current = window.setTimeout(() => {
      lastReported.current = value;
      changeHandler({
        target: {
          value
        }
      });
    }, timeout);
  }, [changeHandler, timeout]);
  useEffect(() => {
    if (lastReported.current !== selected) {
      setValue(selected);
    }
  }, [selected]);
  return [inputValue, onChange];
};
export const useLocationWithoutParams = keysToClear => {
  const location = useLocation();
  return useMemo(() => {
    const search = parseAllParams(location.search);
    const isClearable = keysToClear.some(key => Object.prototype.hasOwnProperty.call(search, key));
    const destination = Object.assign({}, location, {
      search: `?${stringify(keysToClear.reduce((memo, key) => Object.assign(memo, {
        [key]: undefined
      }), search))}`
    });
    return [isClearable, destination];
  }, [location, keysToClear]);
};
export const usePageNumber = () => {
  const location = useLocation();
  let page;
  try {
    page = parseInt(location.match.params.page, 10);
  } catch (e) {
    if (!(e instanceof TypeError)) {
      throw e;
    }
  }
  return page && page > 0 ? page : null;
};

/**
 * Custom hook to set url param and clear other catalog or search params.
 *
 * @returns {[string[], function]} Array with [valueArr, setValue]
 */
export const useSearchParamSingleSelect = () => {
  const keyCats = FILTER_TYPES_OTHER.CATEGORIES;
  const keyTools = FILTER_TYPES_OTHER.TOOLS;
  const history = useHistory();
  const location = useLocation();
  const value = parseValue(location.search);
  const setValue = useCallback((key, newValue) => {
    const allParams = clearSearchFilterParam(clearCatalogFilterParams(parseAllParams(location.search)));
    history.push(Object.assign({}, location, {
      pathname: isPartnersPage(location) ? PARTNER_TRACKS_URL_FRAGMENT : RESULTS_URL_FRAGMENT,
      search: stringify(Object.assign(allParams, key === keyCats ? {
        [keyCats]: newValue
      } : key === keyTools ? {
        [keyTools]: newValue
      } : {}))
    }));
  }, [history, keyCats, keyTools, location]);
  return [value, setValue];
};